import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import { action } from "typesafe-actions";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import {
  IEnrolResult,
  ISequence,
  ISequenceEnrolment,
  ISequenceListRow,
  ISequenceStats,
  ISequenceStep,
  ISequenceStepLogRow,
  ISequenceUpcomingRow,
  StepToType,
} from "./emailSequences.types";

/**
 * Normalise a sequence coming from get-sequence so the builder always works
 * with the new shapes (UI PRD R2):
 *  - §2: `to_type` may be a plain string on older sequences → coerce to array.
 *  - §1: `run_conditions` defaults to null when absent.
 */
const normalizeSequenceForEditor = (seq: ISequence): ISequence => {
  const steps = Array.isArray(seq.steps)
    ? seq.steps.map((step): ISequenceStep => {
        const rawToType = (step as any).to_type;
        let toType: StepToType[] | null;
        if (Array.isArray(rawToType)) {
          toType = rawToType as StepToType[];
        } else if (typeof rawToType === "string" && rawToType) {
          toType = [rawToType as StepToType];
        } else {
          toType = null;
        }
        return {
          ...step,
          to_type: toType,
          to_emails: Array.isArray(step.to_emails) ? step.to_emails : [],
          cc_emails: Array.isArray(step.cc_emails) ? step.cc_emails : [],
          skip_conditions: Array.isArray(step.skip_conditions)
            ? step.skip_conditions
            : [],
          run_conditions: (step as any).run_conditions ?? null,
        };
      })
    : [];
  return { ...seq, steps };
};

/* ================================================================== *
 * SEQUENCE LIST
 * ================================================================== */
export const FETCH_SEQUENCE_LIST_PROGRESS = "FETCH_SEQUENCE_LIST_PROGRESS";
export const FETCH_SEQUENCE_LIST_SUCCESS = "FETCH_SEQUENCE_LIST_SUCCESS";
export const FETCH_SEQUENCE_LIST_FAILED = "FETCH_SEQUENCE_LIST_FAILED";

export const fetchSequenceListProgress = () =>
  action(FETCH_SEQUENCE_LIST_PROGRESS);
export const fetchSequenceListSuccess = (
  data: ISequenceListRow[],
  totalRecords: number
) => action(FETCH_SEQUENCE_LIST_SUCCESS, { data, totalRecords });
export const fetchSequenceListFailed = () =>
  action(FETCH_SEQUENCE_LIST_FAILED);

export interface IFetchSequenceListParams {
  pageNo: number;
  itemPerPage: number;
  module_name?: string;
  trigger_type?: string;
  status?: string;
  column?: string;
  value?: string;
}

export const fetchSequenceListAsync =
  (
    params: IFetchSequenceListParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchSequenceListProgress());
      let url = `/sequence/get-sequence?pageNo=${params.pageNo}&itemPerPage=${params.itemPerPage}`;
      if (params.module_name) url += `&module_name=${params.module_name}`;
      if (params.trigger_type) url += `&trigger_type=${params.trigger_type}`;
      if (params.status && params.status !== "-1")
        url += `&status=${params.status}`;
      if (params.column && params.value) {
        url += `&column=${params.column}&value=${encodeURIComponent(
          params.value
        )}`;
      }
      const res = await api.get(url);
      const data: ISequenceListRow[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchSequenceListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchSequenceListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to load sequences",
          displayAs: "snackbar",
        })
      );
    }
  };

/** Fetch ACTIVE sequences for a module (used by Add-to-sequence pickers). */
export const fetchActiveSequencesByModuleAsync =
  (
    moduleName: string,
    onCallback: (list: ISequenceListRow[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.get(
        `/sequence/get-sequence?module_name=${moduleName}&status=ACTIVE&pageNo=1&itemPerPage=200`
      );
      onCallback(res.data.data || []);
    } catch (err: any) {
      onCallback([]);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to load sequences",
          displayAs: "snackbar",
        })
      );
    }
  };

/* ================================================================== *
 * SINGLE SEQUENCE (builder load)
 * ================================================================== */
export const FETCH_SEQUENCE_PROGRESS = "FETCH_SEQUENCE_PROGRESS";
export const FETCH_SEQUENCE_SUCCESS = "FETCH_SEQUENCE_SUCCESS";
export const FETCH_SEQUENCE_FAILED = "FETCH_SEQUENCE_FAILED";

export const fetchSequenceProgress = () => action(FETCH_SEQUENCE_PROGRESS);
export const fetchSequenceSuccess = (data: ISequence) =>
  action(FETCH_SEQUENCE_SUCCESS, { data });
export const fetchSequenceFailed = () => action(FETCH_SEQUENCE_FAILED);

export const fetchSequenceAsync =
  (sequenceCode: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchSequenceProgress());
      // get-sequence?sequence_code=X returns a one-item array (PRD §6).
      const res = await api.get(
        `/sequence/get-sequence?sequence_code=${sequenceCode}`
      );
      const list: ISequence[] = res.data.data;
      if (list && list.length > 0) {
        dispatch(fetchSequenceSuccess(normalizeSequenceForEditor(list[0])));
      } else {
        dispatch(fetchSequenceFailed());
      }
    } catch (err: any) {
      dispatch(fetchSequenceFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to load sequence",
          displayAs: "snackbar",
        })
      );
    }
  };

/* ================================================================== *
 * UPSERT SEQUENCE (builder save)
 * ================================================================== */
export const upsertSequenceAsync =
  (
    payload: Partial<ISequence>,
    onCallback: (
      isSuccess: boolean,
      sequenceCode?: string,
      errorMessage?: string
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      // status and version are ignored by the server (PRD §6).
      const { status, version, insert_ts, ...body } = payload as ISequence;
      const res = await api.post("/sequence/upsert-sequence", body);
      const saved: ISequence = res.data.data;
      dispatch(
        showMessage({
          type: "success",
          message: "Sequence saved successfully",
          displayAs: "snackbar",
        })
      );
      onCallback(true, saved?.sequence_code || payload.sequence_code || "");
    } catch (err: any) {
      const message =
        err.response?.data?.message || "Failed to save the sequence";
      onCallback(false, undefined, message);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/* ================================================================== *
 * UPDATE SEQUENCE STATUS (activate / deactivate)
 * ================================================================== */
export const updateSequenceStatusAsync =
  (
    sequenceCode: string,
    status: "ACTIVE" | "INACTIVE",
    onCallback: (isSuccess: boolean, errorMessage?: string) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/sequence/update-sequence-status", {
        sequence_code: sequenceCode,
        status,
      });
      dispatch(
        showMessage({
          type: "success",
          message:
            status === "ACTIVE"
              ? "Sequence activated"
              : "Sequence deactivated",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      const message =
        err.response?.data?.message || "Failed to update status";
      onCallback(false, message);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/* ================================================================== *
 * SEQUENCE STATS (detail overview)
 * ================================================================== */
export const FETCH_SEQUENCE_STATS_PROGRESS = "FETCH_SEQUENCE_STATS_PROGRESS";
export const FETCH_SEQUENCE_STATS_SUCCESS = "FETCH_SEQUENCE_STATS_SUCCESS";
export const FETCH_SEQUENCE_STATS_FAILED = "FETCH_SEQUENCE_STATS_FAILED";

export const fetchSequenceStatsProgress = () =>
  action(FETCH_SEQUENCE_STATS_PROGRESS);
export const fetchSequenceStatsSuccess = (data: ISequenceStats) =>
  action(FETCH_SEQUENCE_STATS_SUCCESS, { data });
export const fetchSequenceStatsFailed = () =>
  action(FETCH_SEQUENCE_STATS_FAILED);

export const fetchSequenceStatsAsync =
  (sequenceCode: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchSequenceStatsProgress());
      const res = await api.get(
        `/sequence/get-sequence-stats?sequence_code=${sequenceCode}`
      );
      const data: ISequenceStats = Array.isArray(res.data.data)
        ? res.data.data[0]
        : res.data.data;
      dispatch(fetchSequenceStatsSuccess(data));
    } catch (err: any) {
      dispatch(fetchSequenceStatsFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to load stats",
          displayAs: "snackbar",
        })
      );
    }
  };

/* ================================================================== *
 * SEQUENCE ENROLMENTS (detail tab)
 * ================================================================== */
export const FETCH_SEQUENCE_ENROLMENTS_PROGRESS =
  "FETCH_SEQUENCE_ENROLMENTS_PROGRESS";
export const FETCH_SEQUENCE_ENROLMENTS_SUCCESS =
  "FETCH_SEQUENCE_ENROLMENTS_SUCCESS";
export const FETCH_SEQUENCE_ENROLMENTS_FAILED =
  "FETCH_SEQUENCE_ENROLMENTS_FAILED";

export const fetchSequenceEnrolmentsProgress = () =>
  action(FETCH_SEQUENCE_ENROLMENTS_PROGRESS);
export const fetchSequenceEnrolmentsSuccess = (
  data: ISequenceEnrolment[],
  totalRecords: number
) => action(FETCH_SEQUENCE_ENROLMENTS_SUCCESS, { data, totalRecords });
export const fetchSequenceEnrolmentsFailed = () =>
  action(FETCH_SEQUENCE_ENROLMENTS_FAILED);

export interface IFetchEnrolmentsParams {
  sequence_code: string;
  status?: string;
  module_code?: string;
  pageNo: number;
  itemPerPage: number;
}

export const fetchSequenceEnrolmentsAsync =
  (
    params: IFetchEnrolmentsParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchSequenceEnrolmentsProgress());
      let url = `/sequence/get-sequence-enrollments?sequence_code=${params.sequence_code}&pageNo=${params.pageNo}&itemPerPage=${params.itemPerPage}`;
      if (params.status && params.status !== "-1")
        url += `&status=${params.status}`;
      if (params.module_code) url += `&module_code=${params.module_code}`;
      const res = await api.get(url);
      dispatch(
        fetchSequenceEnrolmentsSuccess(
          res.data.data || [],
          res.data.totalRecords || 0
        )
      );
    } catch (err: any) {
      dispatch(fetchSequenceEnrolmentsFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to load enrolments",
          displayAs: "snackbar",
        })
      );
    }
  };

/* ================================================================== *
 * ENROL (add to sequence)
 * ================================================================== */
export const enrollRecordsAsync =
  (
    payload: {
      sequence_code: string;
      module_name: string;
      module_codes: (string | number)[];
    },
    onCallback: (isSuccess: boolean, results?: IEnrolResult[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      // Chunk to the 500-per-request limit (PRD §5.4).
      const CHUNK = 500;
      const allResults: IEnrolResult[] = [];
      for (let i = 0; i < payload.module_codes.length; i += CHUNK) {
        const chunk = payload.module_codes.slice(i, i + CHUNK);
        const res = await api.post("/sequence/enroll", {
          sequence_code: payload.sequence_code,
          module_name: payload.module_name,
          module_codes: chunk,
        });
        const results: IEnrolResult[] = res.data.data || [];
        allResults.push(...results);
      }
      onCallback(true, allResults);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to enrol records",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/* ================================================================== *
 * PAUSE / RESUME / STOP ENROLMENT
 * ================================================================== */
export const changeEnrolmentStatusAsync =
  (
    enrollmentCode: string,
    action_type: "pause" | "resume" | "stop",
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    const endpointMap = {
      pause: "/sequence/pause-enrollment",
      resume: "/sequence/resume-enrollment",
      stop: "/sequence/stop-enrollment",
    };
    const messageMap = {
      pause: "Enrolment paused",
      resume: "Enrolment resumed",
      stop: "Enrolment stopped",
    };
    try {
      dispatch(saveLoaderProgress());
      await api.post(endpointMap[action_type], {
        enrollment_code: enrollmentCode,
      });
      dispatch(
        showMessage({
          type: "success",
          message: messageMap[action_type],
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to update the enrolment",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/* ================================================================== *
 * RECORD SEQUENCES (record panel)
 * ================================================================== */
export const FETCH_RECORD_SEQUENCES_PROGRESS =
  "FETCH_RECORD_SEQUENCES_PROGRESS";
export const FETCH_RECORD_SEQUENCES_SUCCESS =
  "FETCH_RECORD_SEQUENCES_SUCCESS";
export const FETCH_RECORD_SEQUENCES_FAILED = "FETCH_RECORD_SEQUENCES_FAILED";

export const fetchRecordSequencesProgress = () =>
  action(FETCH_RECORD_SEQUENCES_PROGRESS);
export const fetchRecordSequencesSuccess = (data: ISequenceEnrolment[]) =>
  action(FETCH_RECORD_SEQUENCES_SUCCESS, { data });
export const fetchRecordSequencesFailed = () =>
  action(FETCH_RECORD_SEQUENCES_FAILED);

export const fetchRecordSequencesAsync =
  (
    moduleName: string,
    moduleCode: string | number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchRecordSequencesProgress());
      const res = await api.get(
        `/sequence/get-record-sequences?module_name=${moduleName}&module_code=${moduleCode}`
      );
      dispatch(fetchRecordSequencesSuccess(res.data.data || []));
    } catch (err: any) {
      dispatch(fetchRecordSequencesFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to load record sequences",
          displayAs: "snackbar",
        })
      );
    }
  };

/* ================================================================== *
 * SEQUENCE STEP LOG (record timeline, UI PRD R2 §4)
 * GET /api/v1/sequence/get-sequence-step-log
 * ================================================================== */
export interface IFetchStepLogParams {
  module_name?: string;
  module_code?: string | number;
  enrollment_code?: string;
  sequence_code?: string;
}

export const fetchSequenceStepLogAsync =
  (
    params: IFetchStepLogParams,
    onCallback: (rows: ISequenceStepLogRow[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const qs = new URLSearchParams();
      if (params.module_name) qs.append("module_name", params.module_name);
      if (params.module_code !== undefined && params.module_code !== null)
        qs.append("module_code", String(params.module_code));
      if (params.enrollment_code)
        qs.append("enrollment_code", params.enrollment_code);
      if (params.sequence_code)
        qs.append("sequence_code", params.sequence_code);
      const res = await api.get(
        `/sequence/get-sequence-step-log?${qs.toString()}`
      );
      onCallback(res.data.data || []);
    } catch (err: any) {
      onCallback([]);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to load step log",
          displayAs: "snackbar",
        })
      );
    }
  };

/* ================================================================== *
 * UPCOMING WORKLIST
 * ================================================================== */
export const FETCH_UPCOMING_PROGRESS = "FETCH_UPCOMING_PROGRESS";
export const FETCH_UPCOMING_SUCCESS = "FETCH_UPCOMING_SUCCESS";
export const FETCH_UPCOMING_FAILED = "FETCH_UPCOMING_FAILED";

export const fetchUpcomingProgress = () => action(FETCH_UPCOMING_PROGRESS);
export const fetchUpcomingSuccess = (
  data: ISequenceUpcomingRow[],
  totalRecords: number
) => action(FETCH_UPCOMING_SUCCESS, { data, totalRecords });
export const fetchUpcomingFailed = () => action(FETCH_UPCOMING_FAILED);

export interface IFetchUpcomingParams {
  days: number;
  sequence_code?: string;
  module_name?: string;
  pageNo: number;
  itemPerPage: number;
}

export const fetchUpcomingAsync =
  (
    params: IFetchUpcomingParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchUpcomingProgress());
      let url = `/sequence/get-upcoming?days=${params.days}&pageNo=${params.pageNo}&itemPerPage=${params.itemPerPage}`;
      if (params.sequence_code) url += `&sequence_code=${params.sequence_code}`;
      if (params.module_name) url += `&module_name=${params.module_name}`;
      const res = await api.get(url);
      dispatch(
        fetchUpcomingSuccess(res.data.data || [], res.data.totalRecords || 0)
      );
    } catch (err: any) {
      dispatch(fetchUpcomingFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to load worklist",
          displayAs: "snackbar",
        })
      );
    }
  };

/* ================================================================== *
 * TEMPLATE PREVIEW (render-template)
 * ================================================================== */
export const renderTemplatePreviewAsync =
  (
    templateCode: string,
    onCallback: (
      isSuccess: boolean,
      data?: { subject?: string; body?: string }
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.post("/templates/render-template", {
        template_code: templateCode,
        payload: {},
      });
      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to render preview",
          displayAs: "snackbar",
        })
      );
    }
  };

/* ================================================================== *
 * CLEAR
 * ================================================================== */
export const CLEAR_EMAIL_SEQUENCE = "CLEAR_EMAIL_SEQUENCE";
export const CLEAR_EMAIL_SEQUENCES_STATE = "CLEAR_EMAIL_SEQUENCES_STATE";

export const clearEmailSequence = () => action(CLEAR_EMAIL_SEQUENCE);
export const clearEmailSequencesState = () =>
  action(CLEAR_EMAIL_SEQUENCES_STATE);
